import { memo, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

import type { DataTablePaginationProps } from './data-table.interface'
import { DEFAULT_PAGE_SIZE_OPTION_LIST } from './data-table.mock'
import { DataTablePaginationControls } from './data-table-pagination-controls.atom'
import { DataTablePaginationInfo } from './data-table-pagination-info.atom'
import { useDataTablePagination } from './useDataTablePagination.hook'

/**
 * Renders pagination controls for the data table.
 * Includes page info, page size selector, and navigation buttons.
 */
const DataTablePaginationBase: React.FC<DataTablePaginationProps> = ({
  pagination,
  onPageChange,
  onPageSizeChange,
  pageSizeOptionList = DEFAULT_PAGE_SIZE_OPTION_LIST,
  className,
}): ReactNode => {
  const {
    totalPages,
    startItem,
    endItem,
    isFirstPage,
    isLastPage,
    handleFirstPage,
    handlePrevPage,
    handleNextPage,
    handleLastPage,
    handlePageSizeChange,
  } = useDataTablePagination({ pagination, onPageChange, onPageSizeChange })

  return (
    <div className={cn('data-table-pagination flex items-center justify-between', className)}>
      <DataTablePaginationInfo
        startItem={startItem}
        endItem={endItem}
        totalCount={pagination.totalCount}
        pageSize={pagination.pageSize}
        pageSizeOptionList={pageSizeOptionList}
        onPageSizeChange={onPageSizeChange ? handlePageSizeChange : undefined}
      />
      {onPageChange && totalPages > 1 && (
        <DataTablePaginationControls
          currentPage={pagination.page}
          totalPages={totalPages}
          isFirstPage={isFirstPage}
          isLastPage={isLastPage}
          onFirstPage={handleFirstPage}
          onPrevPage={handlePrevPage}
          onNextPage={handleNextPage}
          onLastPage={handleLastPage}
        />
      )}
    </div>
  )
}

export const DataTablePagination = memo(DataTablePaginationBase)

DataTablePagination.displayName = 'DataTablePagination'
